import { EClickableTarget } from '@/components/atoms/at-clickable/at-clickable.types';
import { EIcons } from '@/components/atoms/at-icon/icons';
import * as yup from 'yup';
import Link from 'next/link';
import { sendGTMNavigationEvent } from '@/components/helpers/analytics/gtm';
import { IFormField } from './or-lead-gen-form.types';
import { ISelectOption } from '@/components/atoms/at-dropdown-field/at-dropdown-field.types';
import { AtCheckboxField } from '@/components/atoms/at-checkbox-field/at-checkbox-field';
import { AtText } from '@/components/atoms/at-text/at-text';

export const moveInDateOptions: Array<ISelectOption> = [
  {
    label: 'As Soon As Possible',
    value: 'As Soon As Possible',
  },
  {
    label: '9-12 Months',
    value: '9-12 Months',
  },
  {
    label: '12-18 Months',
    value: '12-18 Months',
  },
  {
    label: '18+ Months',
    value: '18+ Months',
  },
];

export const isLotOwnerOptions: Array<ISelectOption> = [
  {
    label: 'Yes',
    value: 'Yes',
  },
  {
    label: 'Have a building lot in mind',
    value: 'Have a building lot in mind',
  },
  {
    label: 'Actively Looking',
    value: 'Actively Looking',
  },
  {
    label: 'Not yet',
    value: 'Not yet',
  },
];

export const topicOptions: Array<ISelectOption> = [
  { label: 'Feedback', value: 'Feedback' },
  { label: 'Employment', value: 'Employment' },
  { label: 'Financing', value: 'Financing' },
  { label: 'Finding Land', value: 'Finding Land' },
  { label: 'Housing Plans', value: 'Housing Plans' },
  {
    label: 'Interested in Building a Home',
    value: 'Interested in Building a Home',
  },
  { label: 'Other', value: 'Other' },
  { label: 'Realtor', value: 'Realtor' },
  { label: 'Warranty', value: 'Warranty' },
  { label: 'We Buy Land', value: 'We Buy Land' },
];

const marketingConsentCheckbox = (
  <div key={`field-${'marketingConsent'}`}>
    <AtCheckboxField id="marketingConsent">
      <AtText size="xs">
        By checking this box, you consent to receive promotional SMS/MMS
        messages regarding products & services from Schumacher Homes to the
        number provided. MSG and data rates may apply. MSG frequency varies.
        Text STOP to opt-out. Text HELP for help.
        {/* Note: due to circular dependencies using at clickable in forms (referencing the OrLeadGeneForm in the LeadGenerationContext) i've adjusted this to use Next/Link. */}
        <Link
          href="/terms-of-use"
          className="paragraph-xs !p-0 underline"
          target={EClickableTarget.BLANK}
          onClick={() => {
            sendGTMNavigationEvent(
              'Terms & Conditions',
              'or-lead-gen-form',
              '/terms-of-use',
            );
          }}
        >
          Terms & Conditions
        </Link>{' '}
        |{' '}
        <Link
          href="/privacy-policy"
          className="paragraph-xs !p-0 underline"
          target={EClickableTarget.BLANK}
          onClick={() => {
            sendGTMNavigationEvent(
              'Privacy Policy',
              'or-lead-gen-form',
              '/privacy-policy',
            );
          }}
        >
          {' '}
          Privacy Policy{' '}
        </Link>
      </AtText>
    </AtCheckboxField>
  </div>
);

export const singleStepFormFields = (
  buildLocationOptions: Array<ISelectOption>,
): Array<IFormField> => {
  return [
    {
      id: 'firstName',
      label: 'First Name',
      type: 'text',
      required: true,
    },
    {
      id: 'lastName',
      label: 'Last Name',
      type: 'text',
      required: true,
    },
    {
      id: 'phone',
      label: 'Phone',
      type: 'phone',
      required: true,
    },
    {
      id: 'email',
      label: 'Email',
      type: 'email',
      required: true,
    },
    {
      id: 'buildLocation',
      label: 'Build Location',
      type: 'dropdown',
      required: true,
      options: buildLocationOptions,
      icon: EIcons.LOCATION,
    },
    {
      id: 'moveInDate',
      label: 'Move In Date',
      type: 'dropdown',
      required: true,
      options: moveInDateOptions,
    },
    {
      id: 'isLotOwner',
      label: 'Do you own a lot to build on?',
      type: 'dropdown',
      required: true,
      options: isLotOwnerOptions,
    },
    {
      id: 'marketingConsent',
      type: 'checkbox',
      required: true,
      children: marketingConsentCheckbox,
    },
  ];
};

export const twoStepFormFieldsStepTwo: Array<IFormField> = [
  {
    id: 'firstName',
    label: 'First Name',
    type: 'text',
    required: true,
  },
  {
    id: 'lastName',
    label: 'Last Name',
    type: 'text',
    required: true,
  },
  {
    id: 'email',
    label: 'Email',
    type: 'email',
    required: true,
  },
  {
    id: 'phone',
    label: 'Phone',
    type: 'phone',
    required: true,
  },
  {
    id: 'marketingConsent',
    type: 'checkbox',
    required: true,
    children: marketingConsentCheckbox,
  },
];

export const twoStepFormFieldsStepOne = (
  buildLocationOptions: Array<ISelectOption>,
): Array<IFormField> => {
  return [
    {
      id: 'buildLocation',
      label: 'Build Location',
      type: 'dropdown',
      required: true,
      options: buildLocationOptions,
      icon: EIcons.LOCATION,
    },
    {
      id: 'moveInDate',
      label: 'Move In Date',
      type: 'dropdown',
      required: true,
      options: moveInDateOptions,
    },
    {
      id: 'isLotOwner',
      label: 'Do you own a lot to build on?',
      type: 'dropdown',
      required: true,
      options: isLotOwnerOptions,
    },
  ];
};

export const contactUsFormFields = (
  buildLocationOptions: Array<ISelectOption>,
): Array<IFormField> => {
  return [
    {
      id: 'topic',
      label: 'Topic',
      type: 'dropdown',
      required: false,
      options: topicOptions,
    },
    {
      id: 'firstName',
      label: 'First Name',
      type: 'text',
      required: true,
    },
    {
      id: 'lastName',
      label: 'Last Name',
      type: 'text',
      required: true,
    },
    {
      id: 'email',
      label: 'Email',
      type: 'email',
      required: true,
    },
    {
      id: 'phone',
      label: 'Phone',
      type: 'phone',
      required: true,
    },
    {
      id: 'marketingConsent',
      type: 'checkbox',
      required: true,
      children: marketingConsentCheckbox,
    },
    {
      id: 'buildLocation',
      label: 'Select a Design Studio',
      type: 'dropdown',
      required: false,
      options: buildLocationOptions,
      icon: EIcons.LOCATION,
    },
    {
      id: 'comments',
      label: 'Comments',
      type: 'textarea',
      required: false,
      fullWidth: true,
    },
  ];
};

const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const formSchema = yup
  .object({
    formName: yup.string().required(),
    formId: yup.string().required(),
    formType: yup
      .string()
      .required()
      .oneOf(['single-step', 'two-step', 'download-gate', 'contact-us']),
    firstName: yup.string().required('First Name is required'),
    lastName: yup.string().required('Last Name is required'),
    email: yup
      .string()
      .required('Email is required')
      .email()
      .matches(emailRegex, 'Must be a valid email'),
    phone: yup
      .string()
      .required('Phone number is required')
      .matches(/^[0-9]+$/, 'Phone number must only have digits')
      .length(10, 'Must be a valid phone number'),
    buildLocation: yup.string().when('formType', {
      is: 'contact-us',
      then: (schema: yup.StringSchema) => schema.optional(),
      otherwise: (schema: yup.StringSchema) =>
        schema.required('Please select a location'),
    }),
    moveInDate: yup.string().when('formType', {
      is: 'contact-us',
      then: (schema: yup.StringSchema) => schema.optional(),
      otherwise: (schema: yup.StringSchema) =>
        schema.required('Please select the move in date'),
    }),
    isLotOwner: yup.string().when('formType', {
      is: 'contact-us',
      then: (schema: yup.StringSchema) => schema.optional(),
      otherwise: (schema: yup.StringSchema) =>
        schema.required('Please specify if you own a lot'),
    }),
    marketingConsent: yup.boolean(),
    topic: yup.string(),
    comments: yup.string(),
    contactPageTypeUrl: yup.string(),
    tracking: yup.object({
      gaId: yup.string(),
      gclid: yup.string(),
      msclkid: yup.string(),
      campaign: yup.string(),
      source: yup.string(),
      medium: yup.string(),
      term: yup.string(),
      content: yup.string(),
      fbclid: yup.string(),
      epik: yup.string(),
      tbclid: yup.string(),
    }),
  })
  .required();
